import { MobileDeviceAccessorDef } from "./MobileDeviceAccessorDef";
import { ConversionUtil } from "./ConversionUtil";

export interface DeviceAccessorDef {
    platform: string;
    browser: string;
    accessOrigin: 'web' | 'app';
    isVirtual: () => Promise<boolean>;
    statusBarColor?: (color: string) => void;
    statusBarVisibility?: (visible: boolean) => void;
    statusBarStyle?: (style: 'DARK' | 'LIGHT' | 'DEFAULT') => void;
    statusBarOverlaysWebView?: (doesOverlay: boolean) => void;
    appTesting?: boolean;
}

const appTestingKey = 'app-ui-testing';

export class DeviceAccessor {
    private static _deviceDef: DeviceAccessorDef;
    private static _defaultDeviceDef: DeviceAccessorDef = {
        // default setup is to respond as web.
        platform: getPlatform(),
        browser: getBrowser(),
        accessOrigin: 'web',
        isVirtual: async (): Promise<boolean> => { return false },
    };

    static initialize() {
        DeviceAccessor.setDeviceDef(
            MobileDeviceAccessorDef.hasCapacitor ?
                new MobileDeviceAccessorDef() : null
        );
    }

    private static setDeviceDef(def: DeviceAccessorDef) {
        this._deviceDef = def;
    }

    private static get deviceDef(): DeviceAccessorDef {
        return this._deviceDef ?? this._defaultDeviceDef;
    }

    static get platform(): string {
        return this.deviceDef.platform ?? getPlatform();
    }

    static get browser(): string {
        return this.deviceDef.browser ?? getBrowser();
    }

    private static get accessOrigin(): 'web' | 'app' {
        return this.deviceDef.accessOrigin;
    }

    static get isWeb(): boolean {
        return this.accessOrigin === 'web';
    }

    static get isApp(): boolean {
        return (this.accessOrigin === 'app' && MobileDeviceAccessorDef.hasCapacitor)
            || this.isAppTestingViaWeb();
    }

    static get isiOSApp(): boolean {
        return this.isApp && this.platform === 'ios'
    }

    static get isAndroidApp(): boolean {
        return this.isApp && this.platform === 'android';
    }

    static isAppTestingViaWeb(): boolean {
        if (this.deviceDef.appTesting == null) {
            if (!this.isLocalHost && !this.isMcLeodServer) {
                this.deviceDef.appTesting = false;
            } else {
                const urlParams = new URLSearchParams(window.location.search);
                const paramValue = urlParams.get('app');
                if (paramValue === 'true')
                    sessionStorage.setItem(appTestingKey, 'true');
                this.deviceDef.appTesting = ConversionUtil.parseBoolean(sessionStorage.getItem('app-ui-testing'));
            }
        }
        return this.deviceDef.appTesting;
    }

    static get isLocalHost(): boolean {
        return window.location.origin.toLowerCase().indexOf('localhost') > -1
    }

    static get isMcLeodServer(): boolean {
        return window.location.origin.toLowerCase().indexOf('tmscorp.com') > -1;
    }

    static async isVirtual(): Promise<boolean> {
        return await this.deviceDef.isVirtual();
    }

    /** Valid only for capacitor app */
    static set statusBarColor(color: string) {
        this.deviceDef.statusBarColor?.(color);
    }

    /** Valid only for capacitor app */
    static set statusBarVisibility(visible: boolean) {
        this.deviceDef.statusBarVisibility?.(visible);
    }

    /** Valid only for capacitor app */
    static set statusBarStyle(style: 'DARK' | 'LIGHT' | 'DEFAULT') {
        this.deviceDef.statusBarStyle?.(style);
    }

    /** Valid only for capacitor app */
    static set statusBarOverlaysWebView(doesOverlay: boolean) {
        this.deviceDef.statusBarOverlaysWebView?.(doesOverlay);
    }
}

function getPlatform(): string {
    const platform = navigator.platform?.toLowerCase();
    return platform.indexOf("win") != -1 ? "Windows"
        : platform.indexOf("Mac") != -1 ? "MacOS"
            : platform.indexOf("X11") != -1 ? "UNIX"
                : platform.indexOf("Linux") != -1 ? "Linux"
                    : platform;
}

function getBrowser(): string {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return userAgent.indexOf('edg') > -1 ? 'Edge'
        : userAgent.indexOf('opr') > -1 && !!window["opr"] ? 'Opera'
            : userAgent.indexOf('chrome') > -1 && !!window["chrome"] ? 'Chrome'
                : userAgent.indexOf('trident') > -1 ? 'IE'
                    : userAgent.indexOf('firefox') > -1 ? 'Firefox'
                        : userAgent.indexOf('safari') > -1 ? 'Safari'
                            : window.navigator.userAgent;
}
